import { format } from 'date-fns'

/**
 * Returns whether a property currently has an active (non-cancelled) reservation.
 * @param {string} propertyId
 * @param {Object<string, Array>} reservationsByProperty - Map of propertyId -> reservations[]
 * @returns {boolean}
 */
export const hasCurrentReservation = (propertyId, reservationsByProperty) => {
  if (!propertyId || !reservationsByProperty?.[propertyId]) return false
  const now = new Date()
  return reservationsByProperty[propertyId].some(r => {
    if (r.is_cancelled) return false
    const checkIn = new Date(r.check_in)
    const checkOut = new Date(r.check_out)
    return checkIn <= now && now <= checkOut
  })
}

/**
 * Returns the next incoming (future) reservation for a property.
 * @param {string} propertyId
 * @param {Object<string, Array>} reservationsByProperty
 * @returns {Object|null}
 */
export const getNextCheckInReservation = (propertyId, reservationsByProperty) => {
  if (!propertyId || !reservationsByProperty?.[propertyId]) return null
  const now = new Date()
  return reservationsByProperty[propertyId]
    .filter(r => !r.is_cancelled && new Date(r.check_in) > now)
    .sort((a, b) => new Date(a.check_in) - new Date(b.check_in))[0] || null
}

/**
 * Formats a check-in date string to short display (e.g. "Mar 25, 2026").
 * @param {string} dateStr
 * @returns {string}
 */
export const formatCheckInDate = (dateStr) => {
  if (!dateStr) return ''
  try {
    return format(new Date(dateStr), 'MMM d, yyyy')
  } catch {
    return dateStr
  }
}

/**
 * Sort tier for work order priority grouping.
 * 0 = assigned/started by current user, 1 = urgent, 2 = urgent comments,
 * 3 = in progress, 4 = streamline, 5 = unoccupied, 6 = everything else
 */
const getSortTier = (workOrder, reservationsByProperty, currentUserId) => {
  const assignedToId = Number.parseInt(workOrder.assignedToId, 10)
  if (currentUserId && assignedToId === currentUserId) return 0
  if (workOrder.isUrgent) return 1
  if (workOrder.urgentCommentCount > 0) return 2
  if (workOrder.status === 'IN_PROGRESS') return 3
  if (workOrder.streamlineWorkOrderId) return 4
  if (
    reservationsByProperty?.[workOrder.property?.id] &&
    !hasCurrentReservation(workOrder.property?.id, reservationsByProperty)
  ) {
    return 5
  }
  return 6
}

/**
 * Sorts work orders by priority tiers:
 * 1. Assigned/started by current user
 * 2. Urgent
 * 3. Urgent comments
 * 4. In progress
 * 5. Streamline
 * 6. Unoccupied (by next check-in, soonest first)
 * 7. Everything else
 * @param {Array} workOrders
 * @param {Object<string, Array>} reservationsByProperty
 * @param {number} currentUserId
 * @returns {Array}
 */
export const sortWorkOrdersByPriority = (workOrders, reservationsByProperty, currentUserId) => {
  return [...workOrders].sort((a, b) => {
    const tierA = getSortTier(a, reservationsByProperty, currentUserId)
    const tierB = getSortTier(b, reservationsByProperty, currentUserId)
    if (tierA !== tierB) return tierA - tierB
    if (tierA === 5) {
      const nextA = getNextCheckInReservation(a.property?.id, reservationsByProperty)
      const nextB = getNextCheckInReservation(b.property?.id, reservationsByProperty)
      const dateA = nextA ? new Date(nextA.check_in) : new Date('9999-12-31')
      const dateB = nextB ? new Date(nextB.check_in) : new Date('9999-12-31')
      return dateA - dateB
    }

    const dueA = a.dueDate ? new Date(a.dueDate) : null
    const dueB = b.dueDate ? new Date(b.dueDate) : null
    if (dueA && dueB && dueA.getTime() !== dueB.getTime()) return dueA - dueB
    if (dueA && !dueB) return -1
    if (!dueA && dueB) return 1

    return new Date(b.createdAt) - new Date(a.createdAt)
  })
}
